import { useState, useEffect } from 'react'
import HeaderWithFilters from 'components/HeaderWithFilters'
import CandidateList from 'components/CandidateList'
import Loader from 'components/Loader'
import { useAnalytics } from 'clientHelpers/context/analyticsContext'
import { buildSearchQuery } from 'clientHelpers/api'

const FilterableCandidateList = ({ apiUrl }) => {
  const [candidates, setCandidates] = useState([])
  const [totalCandidates, setTotalCandidates] = useState(process.env.NEXT_PUBLIC_CANDIDATES_ACTIVE_COUNT)
  const [totalCandidatesConstant, setTotalCandidatesConstant] = useState(process.env.NEXT_PUBLIC_CANDIDATES_ACTIVE_COUNT)
  const [filters, setFilters] = useState({})
  const [loadingCandidates, setLoadingCandidates] = useState(true)
  const [error, setError] = useState('')
  const [filtersSelected, setFiltersSelected] = useState({})
  const [filteredByUrl, setFilteredByUrl] = useState(false)

  const { setTotalSearches, totalSearches, setResults, setFilterLists, setCandidatesList, trackEventOnSearch } = useAnalytics()

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  }

  const fetchFilters = async () => {
    const filterResult = await fetch(`${apiUrl}/api/filters`, options)
    const { filters } = await filterResult.json()
    setFilters(filters)
    return filters
  }

  const fetchData = async filterValues => {
    setLoadingCandidates(true)
    const searchQuery = buildSearchQuery(filterValues)
    const candidateResult = await fetch(`${apiUrl}/api/candidates?${searchQuery}`, options)
    const { candidates, totalCandidates, error } = await candidateResult.json()

    if (Object.keys(filterValues).length > 0) {
      // once search has been applied with filters, do the following:
      setTotalSearches(totalSearches + 1)
    } else {
      // only do this when filters have not been applied, that is on first page load
      setTotalCandidatesConstant(totalCandidates)
    }

    setError(error)
    setCandidates(candidates)
    setCandidatesList(candidates)
    setFilterLists(filterValues)
    setResults(totalCandidates)
    setTotalCandidates(totalCandidates)
    setFiltersSelected(filterValues)
    setLoadingCandidates(false)
  }

  useEffect(() => {
    if (Object.keys(filtersSelected).length > 0) {
      const urlParams = new URLSearchParams(window.location.search)
      if ((urlParams.get('locations') || urlParams.get('skills') || urlParams.get('roles')) && !filteredByUrl) {
        setFilteredByUrl(true)
        trackEventOnSearch('applied url search filters')
      } else {
        trackEventOnSearch('applied search filters')
      }
    }
  }, [filtersSelected])

  useEffect(() => {
    window.pageVariantMeasureStart = null
    window.pageVariantMeasureEnd = null
    if (window.pageVariantMeasureStart == null) {
      window.pageVariantMeasureStart = btoa(new Date().getTime() / 1000)
    }

    const initialFetch = async () => {
      const filterList = await fetchFilters()
      const defaultFilters = getQueryParamFilters(filterList)
      await fetchData(defaultFilters)
    }
    initialFetch()
  }, [])

  const mapFilterValues = (category, filterList) => {
    const urlParams = new URLSearchParams(window.location.search)
    const params = urlParams.get(category)
    if (!params) {
      return null
    }
    return params
      .split(',')
      .map(paramValue =>
        filterList[category]?.filter(
          value => paramValue.toLowerCase().trim() === value.name.toLowerCase() || paramValue.trim() === value.country,
        ),
      )
      .flat(1)
      .map(item => ({ name: item.name, value: item.name }))
  }

  const getQueryParamFilters = filterList => {
    const filterSet = {}
    const locationFilters = mapFilterValues('locations', filterList)
    const rolesFilters = mapFilterValues('roles', filterList)
    const skillsFilters = mapFilterValues('skills', filterList)

    if (locationFilters) {
      filterSet.locations = locationFilters
    }

    if (rolesFilters) {
      filterSet.roles = rolesFilters
    }

    if (skillsFilters) {
      filterSet.skills = skillsFilters
    }

    return filterSet
  }

  return (
    <>
      <HeaderWithFilters filtersSelected={filtersSelected} filters={filters} onSearch={fetchData} loading={loadingCandidates} />
      {loadingCandidates && <Loader />}
      <CandidateList
        candidates={candidates}
        totalCandidates={totalCandidates}
        filtersSelected={filtersSelected}
        loading={loadingCandidates}
        error={error}
        totalCandidatesConstant={totalCandidatesConstant}
      />
    </>
  )
}

export default FilterableCandidateList
